import { AxiosResponse, AxiosError, AxiosRequestConfig, RawAxiosResponseHeaders, AxiosResponseHeaders } from 'axios';
import store from '@/store';
import { axios } from '@/plugins/http-axios';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// Убираем 'data' из AxiosRequestConfig чтобы избежать конфликта с нашим 'body'

interface RequestOptions<D = any> extends Omit<AxiosRequestConfig, 'data' | 'method'> {
  method: HttpMethod;
  data?: D;
  body?: D;
}

interface ApiResponse<T = any> extends Pick<AxiosResponse<T>, 'data' | 'status' | 'headers'> {
  data: T;
  status: number;
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
}

interface ApiResponseError {
  errorMessage?: string;
  message?: string;
  [key: string]: any;
}

const request = async <T = any, D = any>(options: RequestOptions<D>): Promise<ApiResponse<T>> => {
  try {
    const methods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    const response: AxiosResponse<T> = await axios({
      ...options,
      url: options.url,
      data: methods.includes(options.method) ? options.body || options.data : undefined,
    });
    return { data: response.data, status: response.status, headers: response.headers };
  } catch (error) {
    const axiosError = error as AxiosError;
    return handleError(axiosError, options);
  }
};

const api = {
  get: <T = any>(options: Omit<RequestOptions, 'method'>) => request<T>({ ...options, method: 'GET' }),
  post: <T = any, D = any>(options: Omit<RequestOptions<D>, 'method'>) => request<T, D>({ ...options, method: 'POST' }),
  put: <T = any, D = any>(options: Omit<RequestOptions<D>, 'method'>) => request<T, D>({ ...options, method: 'PUT' }),
  patch: <T = any, D = any>(options: Omit<RequestOptions<D>, 'method'>) =>
    request<T, D>({ ...options, method: 'PATCH' }),
  delete: <T = any>(options: Omit<RequestOptions, 'method'>) => request<T>({ ...options, method: 'DELETE' }),
};

async function handleError(e: AxiosError, options: RequestOptions) {
  let errorData: ApiResponseError = {};
  if (e.response?.data){
    if (options.responseType === 'blob' && e.response.data instanceof Blob) {
      const blobData: Blob = e.response.data;
      const text = await blobData.text();
      errorData = JSON.parse(text);
      e.response.data = errorData;
    } else {
      errorData = e.response.data as ApiResponseError;
    }
  }

  if (e.response?.status && [409, 404, 400].includes(e.response.status)) {
    store.dispatch('addSuccess', {
      message: errorData?.errorMessage || errorData?.message || 'Ошибка',
      color: 'error',
    });
  } else {
    store.dispatch('setError', e);
  }

  return Promise.reject(e);
}

export { api };
